import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { MapPin, Clock, User, Search, ChevronRight, Plus, Check, Sparkles, Pencil, Trash2, Upload, X } from 'lucide-react';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../hooks/useAuth';
import { useChatSounds } from '../../hooks/useChatSounds';
import { EmojiPicker } from '../chat/EmojiPicker';
import { ActionSheet } from '../ui/ActionMenu';
import { getCut, createCut } from '../../api/cuts';
import { createVibe } from '../../api/vibes';
import { getProject, getProjects } from '../../api/projects';
import { search as searchApi } from '../../api/search';
import { uploadCut } from '../../api/files';
import { resolveCutTarget } from '../../api/ai';
import { mentionSearch } from '../../api/chat';
import type { ChatAiSuggestion, CutSearchResult, MentionSearchResult } from '../../types';

// ─── Module-level pure helpers ───────────────────────────────────────────────

const formatSeconds = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${(safe % 60).toString().padStart(2, '0')}`;
};

const normalizeStr = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

const INFERENCE_STOP_WORDS = new Set([
  'what', 'do', 'you', 'know', 'at', 'like', 'the', 'a', 'an', 'on', 'about', 'this', 'that', 'is', 'it',
]);

const extractCandidateQueries = (text: string): string[] => {
  const candidates = new Set<string>();
  const cleaned = text.trim();
  if (!cleaned) return [];
  const quoted = cleaned.match(/["']([^"']{2,80})["']/g) ?? [];
  for (const raw of quoted) {
    const q = raw.replace(/["']/g, '').trim();
    if (q.length >= 2) candidates.add(q);
  }
  const beforeTime = cleaned.match(/(.+?)\s+at\s+(?:like\s+)?\d{1,4}\s*(?:sec|secs|second|seconds)\b/i);
  if (beforeTime?.[1]) {
    const words = normalizeStr(beforeTime[1]).split(' ').filter((w) => w && !INFERENCE_STOP_WORDS.has(w));
    if (words.length >= 1) { candidates.add(words.slice(-2).join(' ')); candidates.add(words.slice(-3).join(' ')); }
  }
  const beforeClock = cleaned.match(/(.+?)\s+at\s+\d{1,2}:\d{2}\b/i);
  if (beforeClock?.[1]) {
    const words = normalizeStr(beforeClock[1]).split(' ').filter((w) => w && !INFERENCE_STOP_WORDS.has(w));
    if (words.length >= 1) { candidates.add(words.slice(-2).join(' ')); candidates.add(words.slice(-3).join(' ')); }
  }
  const normalized = normalizeStr(cleaned);
  if (normalized.length >= 2 && normalized.split(' ').length <= 5) candidates.add(normalized);
  return Array.from(candidates).filter((c) => c.length >= 2);
};

const scoreCutNameMatch = (cutName: string, hintText: string): number => {
  const nCut = normalizeStr(cutName);
  const nHint = normalizeStr(hintText);
  if (!nCut || !nHint) return 0;
  if (nCut === nHint) return 5;
  if (nHint.includes(nCut) || nCut.includes(nHint)) return 3;
  const hintTokens = new Set(nHint.split(' ').filter(Boolean));
  const cutTokens = new Set(nCut.split(' ').filter(Boolean));
  let overlap = 0;
  for (const token of cutTokens) if (hintTokens.has(token)) overlap++;
  return overlap;
};

const SAFE_MENTION_PATH_RE = /^\/(cuts|projects)\/[A-Za-z0-9_-]+$/;
const MENTION_TOKEN_RE = /@\[([^\]|]+)\|([^\]]+)\]/g;

function renderMessageContent(content: string, isOwn: boolean): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  MENTION_TOKEN_RE.lastIndex = 0;
  while ((match = MENTION_TOKEN_RE.exec(content)) !== null) {
    if (match.index > last) parts.push(content.slice(last, match.index));
    const [, name, path] = match;
    if (SAFE_MENTION_PATH_RE.test(path)) {
      parts.push(
        <Link
          key={match.index}
          to={path}
          className={`font-medium underline underline-offset-2 ${isOwn ? 'text-white/90 hover:text-white' : 'text-primary hover:text-primary/80'}`}
        >
          @{name}
        </Link>
      );
    } else {
      parts.push(`@${name}`);
    }
    last = match.index + match[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>;
}

// ─── Interfaces ──────────────────────────────────────────────────────────────

/** Per-suggestion state for the inline cut picker (all conversation types). */
interface SuggestionPickerState {
  intent: 'CREATE_COMMENT' | 'CREATE_APPROVAL';
  /** All accessible cuts, enriched with vibeId for creation. */
  cutOptions: Array<{ id: string; name: string; vibeName?: string; vibeId: string }>;
  /** Vibes available for cut creation within the selected project. */
  vibeOptions: Array<{ id: string; name: string }>;
  /** null = "create new" mode */
  selectedCutId: string | null;
  /** Name for the new cut (create mode). Pre-filled from AI entity. */
  createCutName: string;
  /** Selected vibe for cut creation (only needed when project has >1 vibe). */
  selectedVibeId: string | null;
  /** Whether the cut list expander is open. */
  cutPickerOpen: boolean;
  /** Filter text for the cut list. */
  cutSearchQuery: string;
  timestamp: number;
  content: string;
  /** True while async inference is running. */
  inferring: boolean;
  /** For DM create-mode: all accessible projects for the project picker. */
  projectOptions: Array<{ id: string; name: string; vibes: Array<{ id: string; name: string }> }>;
  /** For DM create-mode: the project selected for cut creation. */
  selectedProjectId: string | null;
  /** Inline helper for zero-vibe projects: new vibe name. */
  createVibeName: string;
  /** True while creating a vibe inline from suggestion card. */
  creatingVibe: boolean;
}

/** State for the audio upload cut picker */
interface AudioUploadPickerState {
  file: File;
  cutOptions: Array<{ id: string; name: string; vibeName?: string; vibeId: string }>;
  vibeOptions: Array<{ id: string; name: string }>;
  selectedCutId: string | null;
  createCutName: string;
  selectedVibeId: string | null;
  cutPickerOpen: boolean;
  cutSearchQuery: string;
  projectOptions: Array<{ id: string; name: string; vibes: Array<{ id: string; name: string }> }>;
  selectedProjectId: string | null;
  createVibeName: string;
  creatingVibe: boolean;
  uploading: boolean;
}

interface ChatDrawerProps {
  isOpen: boolean;
}

export function ChatDrawer({ isOpen }: ChatDrawerProps) {
  const { user } = useAuth();
  const { playSendSound } = useChatSounds();
  const {
    activeConversationId,
    activeConversation,
    setActiveConversationId,
    chatMessages,
    chatHasMore,
    typingUsers,
    onlineUsers,
    sendChatMessage,
    editChatMessage,
    deleteChatMessage,
    addMessageReaction,
    removeMessageReaction,
    loadMoreMessages,
    socket,
    setChatInputFocused,
    chatExpanded,
    setChatExpanded,
    aiSuggestions,
    dismissAiSuggestion,
    acceptAiSuggestion,
    triggerAiSuggestion,
    aiAcceptedNotices,
    consumeAiAcceptedNotice,
  } = useSocket();

  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);

  // Message action sheet state
  const [actionMenuMessageId, setActionMenuMessageId] = useState<string | null>(null);
  const [capturingMessageId, setCapturingMessageId] = useState<string | null>(null);

  // Inline edit state
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  // Delete confirmation state
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  
  // Quick reactions (shown on hover/long-press)
  const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Emoji picker for reactions (Enhancement #3)
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState<string | null>(null);
  // Per-suggestion inline cut picker state (all conversation types)
  const [pickerStates, setPickerStates] = useState<Map<string, SuggestionPickerState>>(new Map());
  const [submittingSuggestionId, setSubmittingSuggestionId] = useState<string | null>(null);
  const inferenceStartedRef = useRef<Set<string>>(new Set());
  // Keep a stable ref to activeMessages so inference useEffect doesn't need it as a dep
  // Initialized empty; kept in sync each render below (see "activeMessagesRef.current = activeMessages")
  const activeMessagesRef = useRef<typeof activeMessages>([]);

  // Audio upload picker state
  const [audioUploadPicker, setAudioUploadPicker] = useState<AudioUploadPickerState | null>(null);
  const [audioInputRef] = useState<React.RefObject<HTMLInputElement | null>>(() => ({ current: null }));

  // Mention autocomplete state
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionAtPos, setMentionAtPos] = useState<number>(-1);
  const [mentionResults, setMentionResults] = useState<MentionSearchResult[]>([]);
  const [mentionLoading, setMentionLoading] = useState(false);

  // Derive conversation metadata
  const convId = activeConversationId;
  const conv = activeConversation;
  const isDM = conv?.type === 'DM';

  // For DMs, the "other" person is the single member that isn't us
  const otherMember = isDM
    ? conv?.members.find((m) => m.id !== user?.id) ?? null
    : null;

  // Header display
  const headerName = isDM
    ? otherMember?.name ?? 'Chat'
    : conv?.name ?? 'Group Chat';

  const headerAvatarName = isDM ? otherMember?.name ?? '?' : headerName;
  const headerAvatarUrl = isDM ? otherMember?.avatarUrl : null;

  // Online status (for DMs, check the other member)
  const isOnline = isDM
    ? otherMember ? onlineUsers.has(otherMember.id) || otherMember.isOnline : false
    : false;

  // Messages for the active conversation
  const activeMessages = convId ? (chatMessages[convId] ?? []) : [];
  const activeAiSuggestions = convId ? (aiSuggestions[convId] ?? []) : [];
  const activeAiAcceptedNotices = convId ? (aiAcceptedNotices[convId] ?? []) : [];

  // Typing: for the active conversation, get userIds that are typing (excluding self)
  const typingSet = convId ? typingUsers[convId] : undefined;
  const typingUserIds = typingSet ? Array.from(typingSet).filter((id) => id !== user?.id) : [];
  const isAnyoneTyping = typingUserIds.length > 0;

  // Build typing label (for groups, show names)
  const typingLabel = isDM
    ? `${otherMember?.name?.split(' ')[0] ?? 'Someone'} is typing`
    : typingUserIds.length === 1
      ? `${conv?.members.find((m) => m.id === typingUserIds[0])?.name?.split(' ')[0] ?? 'Someone'} is typing`
      : `${typingUserIds.length} people are typing`;

  // Scroll to bottom when new messages, AI suggestions, or accepted notices arrive
  useEffect(() => {
    if (isOpen && convId) {
      messagesEndRef.current?.scrollIntoView();
    }
  }, [activeMessages.length, activeAiSuggestions.length, activeAiAcceptedNotices.length, isOpen, convId]);

  // Detect mobile viewport for chat drawer entry/exit animation direction
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Focus edit input when entering edit mode
  useEffect(() => {
    if (editingMessageId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.setSelectionRange(editValue.length, editValue.length);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingMessageId]);

  // Handle send
  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || !convId || isSending) return;
    const text = inputValue.trim();
    setInputValue('');
    setIsSending(true);
    // Stop typing indicator
    if (isTypingRef.current) {
      socket?.emit('chat:typing', { conversationId: convId, isTyping: false });
      isTypingRef.current = false;
    }
    try {
      await sendChatMessage(convId, text);
      playSendSound();
    } finally {
      setIsSending(false);
    }
  }, [inputValue, convId, isSending, sendChatMessage, socket, playSendSound]);

  // Handle typing indicator + @ mention detection
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setInputValue(value);

      // Detect active @mention query from text before cursor
      const cursor = e.target.selectionStart ?? value.length;
      const before = value.slice(0, cursor);
      const atMatch = /@([^@\n]*)$/.exec(before);
      if (atMatch && conv?.projectId) {
        setMentionQuery(atMatch[1]);
        setMentionAtPos(atMatch.index);
      } else {
        setMentionQuery(null);
        setMentionAtPos(-1);
        setMentionResults([]);
      }

      if (!convId || !socket) return;
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        socket.emit('chat:typing', { conversationId: convId, isTyping: true });
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (isTypingRef.current) {
          isTypingRef.current = false;
          socket.emit('chat:typing', { conversationId: convId, isTyping: false });
        }
      }, 2000);
    },
    [convId, socket, conv?.projectId]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && mentionResults.length > 0) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        setMentionAtPos(-1);
        setMentionResults([]);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && mentionQuery === null) {
      e.preventDefault();
      handleSend();
    }
  };

  const insertMention = useCallback(
    (result: MentionSearchResult) => {
      const safeName = result.name.replace(/[|[\]]/g, ' ').trim();
      const token = `@[${safeName}|${result.path}]`;
      const before = inputValue.slice(0, mentionAtPos);
      const after = inputValue.slice(mentionAtPos + 1 + (mentionQuery?.length ?? 0));
      const newValue = `${before}${token} ${after}`;
      setInputValue(newValue);
      setMentionQuery(null);
      setMentionAtPos(-1);
      setMentionResults([]);
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          const pos = before.length + token.length + 1;
          textareaRef.current.setSelectionRange(pos, pos);
          textareaRef.current.focus();
        }
      });
    },
    [inputValue, mentionAtPos, mentionQuery]
  );

  // Debounced mention search
  useEffect(() => {
    if (mentionQuery === null || !convId) return;
    const timer = setTimeout(async () => {
      setMentionLoading(true);
      try {
        const { results } = await mentionSearch(convId, mentionQuery);
        setMentionResults(results);
      } catch {
        setMentionResults([]);
      } finally {
        setMentionLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [mentionQuery, convId]);

  // ─── Audio upload picker helpers ───────────────────────────────────────────

  const initAudioUploadPicker = useCallback(async (): Promise<Omit<AudioUploadPickerState, 'file' | 'uploading'>> => {
    const projectId = conv?.projectId ?? null;
    let vibeOptions: Array<{ id: string; name: string }> = [];
    let projectOptions: Array<{ id: string; name: string; vibes: Array<{ id: string; name: string }> }> = [];
    let cutOptions: Array<{ id: string; name: string; vibeName?: string; vibeId: string }>;

    if (projectId) {
      const project = await getProject(projectId);
      vibeOptions = (project.vibes ?? []).map((v) => ({ id: v.id, name: v.name }));
      cutOptions = (project.vibes ?? []).flatMap((vibe) =>
        (vibe.cuts ?? []).map((cut) => ({
          id: cut.id,
          name: cut.name,
          vibeName: vibe.name,
          vibeId: vibe.id,
        }))
      );
    } else {
      const projects = await getProjects();
      const hydrated = await Promise.all(projects.map((p) => getProject(p.id)));
      projectOptions = hydrated.map((p) => ({
        id: p.id,
        name: p.name,
        vibes: (p.vibes ?? []).map((v) => ({ id: v.id, name: v.name })),
      }));
      cutOptions = hydrated.flatMap((p) =>
        (p.vibes ?? []).flatMap((vibe) =>
          (vibe.cuts ?? []).map((cut) => ({
            id: cut.id,
            name: cut.name,
            vibeName: vibe.name,
            vibeId: vibe.id,
          }))
        )
      );
    }

    return { cutOptions, vibeOptions, projectOptions, selectedCutId: null, createCutName: '', selectedVibeId: null, cutPickerOpen: false, cutSearchQuery: '', selectedProjectId: null, createVibeName: '', creatingVibe: false };
  }, [conv?.projectId]);

  const handleAudioFileSelected = useCallback(async (file: File) => {
    try {
      const options = await initAudioUploadPicker();
      setAudioUploadPicker({ ...options, file, uploading: false });
    } catch (error) {
      console.error('Failed to initialize audio upload picker:', error);
      toast.error('Failed to load cut options');
    }
  }, [initAudioUploadPicker]);

  const updateAudioUploadPicker = useCallback((updates: Partial<AudioUploadPickerState>) => {
    setAudioUploadPicker((prev) => prev ? { ...prev, ...updates } : null);
  }, []);

  const handleAudioUpload = useCallback(async () => {
    if (!audioUploadPicker || !convId) return;
    const { file, selectedCutId, createCutName, selectedVibeId, selectedProjectId, cutOptions } = audioUploadPicker;

    // Validate
    const isCreateMode = selectedCutId === null;
    const isDMConv = !conv?.projectId;
    const resolvedVibeId = selectedVibeId ?? (audioUploadPicker.vibeOptions.length === 1 ? audioUploadPicker.vibeOptions[0].id : null);
    const dmProjectResolved = !isDMConv || !isCreateMode || !!selectedProjectId;

    if (isCreateMode) {
      if (!createCutName.trim() || !resolvedVibeId || !dmProjectResolved) return;
    } else {
      if (selectedCutId === null) return;
    }

    updateAudioUploadPicker({ uploading: true });

    try {
      let targetCutId = selectedCutId;
      let targetCutName = '';

      if (isCreateMode) {
        // Create new cut
        if (resolvedVibeId) {
          const cut = await createCut(resolvedVibeId, { name: createCutName.trim() });
          targetCutId = cut.id;
          targetCutName = cut.name;
        }
      } else {
        const cut = cutOptions.find((c) => c.id === selectedCutId);
        targetCutName = cut?.name ?? '';
      }

      // Upload file
      await uploadCut(targetCutId!, file, { name: file.name });

      // Send chat message
      const message = ` uploaded audio "${file.name}" to cut "${targetCutName}"`;
      await sendChatMessage(convId, message);

      toast.success('Audio uploaded successfully');
      setAudioUploadPicker(null);
    } catch (error) {
      console.error('Failed to upload audio:', error);
      toast.error('Failed to upload audio');
      updateAudioUploadPicker({ uploading: false });
    }
  }, [audioUploadPicker, convId, conv?.projectId, updateAudioUploadPicker, sendChatMessage]);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el || !convId) return;
    if (el.scrollTop === 0 && chatHasMore[convId]) {
      loadMoreMessages(convId);
    }
  }, [convId, chatHasMore, loadMoreMessages]);

  const openMessageActions = useCallback((messageId: string) => {
    setActionMenuMessageId(messageId);
  }, []);

  const startEdit = useCallback((messageId: string, currentContent: string) => {
    setActionMenuMessageId(null);
    setEditingMessageId(messageId);
    setEditValue(currentContent);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditValue('');
  }, []);

  const submitEdit = useCallback(async () => {
    if (!editingMessageId || !editValue.trim() || !convId) return;
    const msg = activeMessages.find((m) => m.id === editingMessageId);
    // Skip if content unchanged
    if (msg && msg.content === editValue.trim()) {
      cancelEdit();
      return;
    }
    await editChatMessage(convId, editingMessageId, editValue.trim());
    cancelEdit();
  }, [editingMessageId, editValue, convId, activeMessages, editChatMessage, cancelEdit]);

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitEdit();
    }
    if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const confirmDelete = useCallback((messageId: string) => {
    setActionMenuMessageId(null);
    setDeletingMessageId(messageId);
  }, []);

  const executeDelete = useCallback(async () => {
    if (!deletingMessageId || !convId) return;
    await deleteChatMessage(convId, deletingMessageId);
    setDeletingMessageId(null);
  }, [deletingMessageId, convId, deleteChatMessage]);

  const handleReactionClick = useCallback(async (messageId: string, emoji: string, alreadyReacted: boolean) => {
    if (!convId) return;
    if (alreadyReacted) {
      await removeMessageReaction(convId, messageId, emoji);
    } else {
      await addMessageReaction(convId, messageId, emoji);
    }
  }, [convId, addMessageReaction, removeMessageReaction]);

  const handleCaptureWithAi = useCallback(async (messageId: string) => {
    if (capturingMessageId) return;
    setCapturingMessageId(messageId);
    try {
      const status = await triggerAiSuggestion(messageId);
      if (status === 'EXISTING') {
        toast.info('Suggestion already pending for this message');
      } else {
        toast.success('AI suggestion created');
      }
    } catch (error) {
      console.error('Failed to trigger AI suggestion:', error);
      toast.error('Failed to capture with AI');
    } finally {
      setCapturingMessageId(null);
    }
  }, [triggerAiSuggestion, capturingMessageId]);

  // Mobile long-press handlers for message action menu
  const handleTouchStart = useCallback((messageId: string) => {
    longPressTimerRef.current = setTimeout(() => {
      openMessageActions(messageId);
      // Haptic feedback if available
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    }, 500);
  }, [openMessageActions]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleTouchCancel = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // Close reaction picker on click outside
  useEffect(() => {
    if (!reactionPickerMessageId) return;
    const handleClickOutside = () => setReactionPickerMessageId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [reactionPickerMessageId]);

  const renderAvatar = (name: string, avatarUrl?: string | null, small = false) => {
    const size = small ? 'w-6 h-6 text-[9px]' : 'w-8 h-8 text-xs';
    const initials = name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
    if (avatarUrl) {
      return (
        <img
          src={avatarUrl}
          alt={name}
          referrerPolicy="no-referrer"
          className={`${size} rounded-full object-cover flex-shrink-0`}
        />
      );
    }
    return (
      <div
        className={`${size} rounded-full bg-primary/20 text-primary flex items-center justify-center font-semibold flex-shrink-0`}
      >
        {initials}
      </div>
    );
  };

  /** Render a stacked avatar group for group conversations */
  const renderGroupAvatar = () => {
    const members = conv?.members.filter((m) => m.id !== user?.id).slice(0, 3) ?? [];
    if (members.length === 0) return renderAvatar(headerName);
    if (members.length === 1) return renderAvatar(members[0].name, members[0].avatarUrl);

    return (
      <div className="relative w-8 h-8 flex-shrink-0">
        <div className="absolute top-0 left-0 w-5 h-5">
          {renderAvatar(members[0].name, members[0].avatarUrl, true)}
        </div>
        <div className="absolute bottom-0 right-0 w-5 h-5 border border-surface rounded-full">
          {renderAvatar(members[1].name, members[1].avatarUrl, true)}
        </div>
      </div>
    );
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateLabel = (iso: string) => {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Group messages by date
  const groupedMessages: { label: string; messages: typeof activeMessages }[] = [];
  let lastLabel = '';
  for (const msg of activeMessages) {
    const label = formatDateLabel(msg.createdAt);
    if (label !== lastLabel) {
      groupedMessages.push({ label, messages: [msg] });
      lastLabel = label;
    } else {
      groupedMessages[groupedMessages.length - 1].messages.push(msg);
    }
  }

  // Keep activeMessagesRef in sync (so inference can read it without being a dep)
  activeMessagesRef.current = activeMessages;

  /** Find the sender display info for a message in a group conversation */
  const getSenderInfo = (senderId: string) => {
    const member = conv?.members.find((m) => m.id === senderId);
    return member ?? { id: senderId, name: 'Unknown', avatarUrl: null, role: 'member' as const, isOnline: false };
  };

  // ─── Inline cut picker: inference + state management ───────────────────────

  /**
   * Run cut inference for a suggestion in a project conversation.
   * Always fetches the project first so vibeOptions are available for cut creation.
   */
  const runInference = useCallback(async (
    suggestion: ChatAiSuggestion,
    projectId: string | null,
  ): Promise<{
    inferredCutId: string | null;
    cutOptions: Array<{ id: string; name: string; vibeName?: string; vibeId: string }>;
    vibeOptions: Array<{ id: string; name: string }>;
    projectOptions: Array<{ id: string; name: string; vibes: Array<{ id: string; name: string }> }>;
  }> => {
    const msgs = activeMessagesRef.current;
    const suggestionText = suggestion.intent === 'CREATE_APPROVAL'
      ? (suggestion.draft.approvalText ?? '')
      : (suggestion.draft.commentText ?? '');
    const sourceMessage = msgs.find((m) => m.id === suggestion.messageId);

    // Fetch cut/vibe options — scoped to project for group convs, global for DMs
    let vibeOptions: Array<{ id: string; name: string }> = [];
    let projectOptions: Array<{ id: string; name: string; vibes: Array<{ id: string; name: string }> }> = [];
    let cutOptions: Array<{ id: string; name: string; vibeName?: string; vibeId: string }>;

    if (projectId) {
      const project = await getProject(projectId);
      vibeOptions = (project.vibes ?? []).map((v) => ({ id: v.id, name: v.name }));
      cutOptions = (project.vibes ?? []).flatMap((vibe) =>
        (vibe.cuts ?? []).map((cut) => ({
          id: cut.id,
          name: cut.name,
          vibeName: vibe.name,
          vibeId: vibe.id,
        }))
      );
    } else {
      // DM: load all accessible projects + cuts globally
      const projects = await getProjects();
      const hydrated = await Promise.all(projects.map((p) => getProject(p.id)));
      projectOptions = hydrated.map((p) => ({
        id: p.id,
        name: p.name,
        vibes: (p.vibes ?? []).map((v) => ({ id: v.id, name: v.name })),
      }));
      cutOptions = hydrated.flatMap((p) =>
        (p.vibes ?? []).flatMap((vibe) =>
          (vibe.cuts ?? []).map((cut) => ({
            id: cut.id,
            name: cut.name,
            vibeName: vibe.name,
            vibeId: vibe.id,
          }))
        )
      );
      // No vibeOptions at this point — populated after user picks a project in create-mode
    }

    // 0) If user is currently on a cut page within the accessible cuts, prefer that cut
    if (typeof window !== 'undefined') {
      const cutMatch = window.location.pathname.match(/^\/cuts\/([^/?#]+)/);
      if (cutMatch?.[1] && cutOptions.some((c) => c.id === cutMatch[1])) {
        return { inferredCutId: cutMatch[1], cutOptions, vibeOptions, projectOptions };
      }
    }

    // 1) Score cuts by name match — only use the AI-extracted cutName entity,
    //    not the full message/draft text, to avoid false positives.
    const cutNameHint = suggestion.entities.cutName
      ? normalizeStr(suggestion.entities.cutName)
      : '';

    if (cutNameHint && cutOptions.length > 0) {
      const ranked = cutOptions
        .map((cut) => ({ ...cut, score: scoreCutNameMatch(cut.name, cutNameHint) }))
        .sort((a, b) => b.score - a.score);
      if (ranked[0].score > 0) {
        return { inferredCutId: ranked[0].id, cutOptions, vibeOptions, projectOptions };
      }
    }

    // 2) Try global search — only when we have a specific cutName entity to search for
    const queryCandidates = suggestion.entities.cutName
      ? extractCandidateQueries(suggestion.entities.cutName)
      : [];

    for (const query of queryCandidates) {
      try {
        const result = await searchApi(query);
        const cutGroup = result.groups.find((g) => g.type === 'cut');
        const cuts = (cutGroup?.results ?? []) as CutSearchResult[];
        // Only accept if the found cut is in our accessible cuts
        const inScope = (c: CutSearchResult) => cutOptions.some((o) => o.id === c.id);

        if (cuts.length === 1 && inScope(cuts[0])) {
          return { inferredCutId: cuts[0].id, cutOptions, vibeOptions, projectOptions };
        }
        if (cuts.length > 1 && suggestion.entities.cutName) {
          const target = normalizeStr(suggestion.entities.cutName);
          const exact = cuts.find((c) => normalizeStr(c.title) === target && inScope(c));
          if (exact) return { inferredCutId: exact.id, cutOptions, vibeOptions, projectOptions };
          const partial = cuts.find((c) =>
            (normalizeStr(c.title).includes(target) || target.includes(normalizeStr(c.title))) && inScope(c)
          );
          if (partial) return { inferredCutId: partial.id, cutOptions, vibeOptions, projectOptions };
        }
      } catch (err) {
        console.error('Search inference failed:', err);
      }
    }

    // 3) AI resolver as last resort — only when we have a cutName entity to anchor on
    const aiInput = [sourceMessage?.content, suggestionText].filter(Boolean).join('\n');
    if (aiInput.trim() && cutOptions.length > 0 && suggestion.entities.cutName && projectId) {
      try {
        const aiResolved = await resolveCutTarget({
          message: aiInput,
          projectId,
          suggestedCutName: suggestion.entities.cutName,
          candidateCuts: cutOptions.map((c) => ({ id: c.id, name: c.name, vibeName: c.vibeName })),
        });
        if (aiResolved.cutId && aiResolved.confidence >= 0.6) {
          return { inferredCutId: aiResolved.cutId, cutOptions, vibeOptions, projectOptions };
        }
      } catch (err) {
        console.error('AI resolver failed:', err);
      }
    }

    return { inferredCutId: null, cutOptions, vibeOptions, projectOptions };
  }, []);

  /**
   * Eager inference: when new suggestions appear in a project conversation,
   * kick off inference immediately so the picker is pre-populated.
   */
  useEffect(() => {
    const projectId = conv?.projectId ?? null;

    // Clean up state for dismissed suggestions
    const activeIds = new Set(activeAiSuggestions.map((s) => s.id));
    setPickerStates((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const id of next.keys()) {
        if (!activeIds.has(id)) { next.delete(id); changed = true; }
      }
      return changed ? next : prev;
    });
    // Clean up inference tracking for dismissed suggestions
    for (const id of Array.from(inferenceStartedRef.current)) {
      if (!activeIds.has(id)) inferenceStartedRef.current.delete(id);
    }

    // Start inference for new suggestions
    for (const suggestion of activeAiSuggestions) {
      if (inferenceStartedRef.current.has(suggestion.id)) continue;
      inferenceStartedRef.current.add(suggestion.id);

      const msgs = activeMessagesRef.current;
      const suggestionText = suggestion.intent === 'CREATE_APPROVAL'
        ? (suggestion.draft.approvalText ?? '')
        : (suggestion.draft.commentText ?? '');
      const sourceMessage = msgs.find((m) => m.id === suggestion.messageId);
      const content = (suggestionText || sourceMessage?.content || '').trim();
      const ts = suggestion.entities.timestampSeconds ?? 0;

      // Initialize with inferring=true immediately
      setPickerStates((prev) => {
        if (prev.has(suggestion.id)) return prev;
        const next = new Map(prev);
        next.set(suggestion.id, {
          intent: suggestion.intent,
          cutOptions: [],
          vibeOptions: [],
          projectOptions: [],
          selectedProjectId: null,
          selectedCutId: null,
          createCutName: suggestion.entities.cutName ?? '',
          selectedVibeId: null,
          createVibeName: '',
          creatingVibe: false,
          cutPickerOpen: false,
          cutSearchQuery: '',
          timestamp: Number.isFinite(ts) ? Math.max(0, Math.round(ts)) : 0,
          content,
          inferring: true,
        });
        return next;
      });

      // Run inference async and populate the picker
      runInference(suggestion, projectId)
        .then((result) => {
          setPickerStates((prev) => {
            const existing = prev.get(suggestion.id);
            if (!existing) return prev; // dismissed while inferring
            const initialDmProject = projectId
              ? null
              : (result.projectOptions.find((p) => p.vibes.length > 0) ?? result.projectOptions[0] ?? null);
            const next = new Map(prev);
            next.set(suggestion.id, {
              ...existing,
              cutOptions: result.cutOptions,
              vibeOptions: initialDmProject ? initialDmProject.vibes : result.vibeOptions,
              projectOptions: result.projectOptions,
              selectedProjectId: initialDmProject ? initialDmProject.id : existing.selectedProjectId,
              selectedVibeId: initialDmProject && initialDmProject.vibes.length === 1
                ? initialDmProject.vibes[0].id
                : existing.selectedVibeId,
              selectedCutId: result.inferredCutId,
              // Auto-open if no match found and there are cuts to choose from
              cutPickerOpen: result.inferredCutId === null && result.cutOptions.length > 0,
              inferring: false,
            });
            return next;
          });
        })
        .catch((err) => {
          console.error('Picker inference error:', err);
          setPickerStates((prev) => {
            const existing = prev.get(suggestion.id);
            if (!existing) return prev;
            const next = new Map(prev);
            next.set(suggestion.id, { ...existing, inferring: false, cutPickerOpen: true });
            return next;
          });
        });
    }
  }, [activeAiSuggestions, conv?.projectId, runInference]);

  /** Helper to update a single field (or many) on a suggestion's picker state. */
  const updatePickerState = useCallback((
    suggestionId: string,
    updates: Partial<SuggestionPickerState>,
  ) => {
    setPickerStates((prev) => {
      const existing = prev.get(suggestionId);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(suggestionId, { ...existing, ...updates });
      return next;
    });
  }, []);

  /** Create a vibe inline from DM create-mode when selected project has none. */
  const handleCreateVibeInline = useCallback(async (
    suggestionId: string,
    picker: SuggestionPickerState,
  ) => {
    const projectId = picker.selectedProjectId;
    const vibeName = picker.createVibeName.trim();
    if (!projectId) {
      toast.error('Select a project first');
      return;
    }
    if (!vibeName) {
      toast.error('Enter a vibe name');
      return;
    }

    updatePickerState(suggestionId, { creatingVibe: true });
    try {
      const created = await createVibe(projectId, { name: vibeName });
      setPickerStates((prev) => {
        const existing = prev.get(suggestionId);
        if (!existing) return prev;
        const next = new Map(prev);
        const projectOptions = existing.projectOptions.map((p) =>
          p.id === projectId
            ? { ...p, vibes: [...p.vibes, { id: created.id, name: created.name }] }
            : p,
        );
        const selectedProject = projectOptions.find((p) => p.id === projectId);
        next.set(suggestionId, {
          ...existing,
          projectOptions,
          vibeOptions: selectedProject?.vibes ?? existing.vibeOptions,
          selectedVibeId: created.id,
          createVibeName: '',
          creatingVibe: false,
        });
        return next;
      });

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('bandmate:project-data-changed', {
          detail: { projectId, vibeId: created.id },
        }));
      }
      toast.success(`Created vibe "${created.name}"`);
    } catch {
      toast.error('Failed to create vibe');
      updatePickerState(suggestionId, { creatingVibe: false });
    }
  }, [updatePickerState]);

  /** Accept handler for all conversations — creates cut if needed, then saves. */
  const handleAcceptForProject = useCallback(async (
    suggestion: ChatAiSuggestion,
    picker: SuggestionPickerState,
  ) => {
    if (!convId) return;

    let finalCutId = picker.selectedCutId;

    // Create cut if in "create new" mode
    if (finalCutId === null) {
      const cutName = picker.createCutName.trim() || picker.cutSearchQuery.trim();
      if (!cutName) {
        toast.error('Enter a cut name to create');
        return;
      }
      // For DMs, the project is chosen via selectedProjectId; for group convs, use the conv's project
      // Resolve vibeId: explicit selection first, then fall back to single-vibe project
      let vibeId = picker.selectedVibeId;
      if (!vibeId) {
        if (picker.vibeOptions.length === 1) {
          vibeId = picker.vibeOptions[0].id;
        } else if (!conv?.projectId && picker.selectedProjectId) {
          // DM: find vibes for the selected project from projectOptions
          const proj = picker.projectOptions.find((p) => p.id === picker.selectedProjectId);
          if (proj && proj.vibes.length === 1) vibeId = proj.vibes[0].id;
        }
      }
      if (!vibeId) {
        toast.error('Select a vibe to create the cut in');
        return;
      }
      try {
        setSubmittingSuggestionId(suggestion.id);
        const newCut = await createCut(vibeId, { name: cutName });
        const projectIdForSync = conv?.projectId ?? picker.selectedProjectId;
        if (typeof window !== 'undefined' && projectIdForSync) {
          window.dispatchEvent(new CustomEvent('bandmate:project-data-changed', {
            detail: { projectId: projectIdForSync, vibeId, cutId: newCut.id },
          }));
        }
        finalCutId = newCut.id;
      } catch {
        toast.error('Failed to create cut');
        setSubmittingSuggestionId(null);
        return;
      }
    }

    try {
      setSubmittingSuggestionId(suggestion.id);
      const cut = await getCut(finalCutId);
      const defaultFile = cut.managedFiles?.find((f) => f.type === 'CUT');

      if (defaultFile?.id) {
        // Full path: pin comment to the audio file
        const content = picker.content.trim();
        if (!content) {
          toast.error('Comment text is required');
          return;
        }
        await acceptAiSuggestion(suggestion.id, {
          cutId: cut.id,
          managedFileId: defaultFile.id,
          timestamp: picker.timestamp,
          content,
        });
        if (convId) {
          consumeAiAcceptedNotice(
            convId,
            `AI saved this to ${cut.name} at ${formatSeconds(picker.timestamp)}${picker.intent === 'CREATE_APPROVAL' ? ' and requested approval.' : '.'}`,
          );
        }
      } else {
        // No-file path: accept for training + cut association, skip comment creation
        await acceptAiSuggestion(suggestion.id, { cutId: cut.id });
        // Notice text comes via socket (chat:aiSuggestionAcceptedNotice); no local notice needed
      }
    } catch {
      toast.error('Failed to save suggestion');
    } finally {
      setSubmittingSuggestionId(null);
    }
  }, [convId, conv?.projectId, acceptAiSuggestion, consumeAiAcceptedNotice]);

  const inputPlaceholder = isDM
    ? `Message ${otherMember?.name?.split(' ')[0] ?? ''}…`
    : `Message ${headerName}…`;

  const selectedActionMessage = actionMenuMessageId
    ? activeMessages.find((m) => m.id === actionMenuMessageId) ?? null
    : null;
  const selectedActionIsOwn = selectedActionMessage?.senderId === user?.id;
  const actionMenuItems = selectedActionMessage
    ? [
        {
          label: 'Capture with AI',
          description: capturingMessageId === selectedActionMessage.id ? 'Creating suggestion…' : 'Create an AI suggestion from this message',
          icon: <Sparkles className="w-5 h-5" />,
          onClick: () => void handleCaptureWithAi(selectedActionMessage.id),
          visible: true,
        },
        {
          label: 'Edit',
          description: 'Update this message text',
          icon: <Pencil className="w-5 h-5" />,
          onClick: () => startEdit(selectedActionMessage.id, selectedActionMessage.content),
          visible: selectedActionIsOwn,
        },
        {
          label: 'Delete',
          description: 'Remove this message for everyone',
          icon: <Trash2 className="w-5 h-5" />,
          onClick: () => confirmDelete(selectedActionMessage.id),
          variant: 'danger' as const,
          visible: selectedActionIsOwn,
        },
      ]
    : [];

  return (
    <AnimatePresence>
      {(isOpen && convId) && (
        <>
          <motion.div
            key="chat-backdrop"
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setActiveConversationId(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            aria-hidden="true"
          />
          <motion.div
            key="chat-drawer"
            data-chat-drawer="true"
            className={`fixed top-0 left-0 w-full h-full bg-surface z-50 flex flex-col shadow-xl lg:left-64 lg:border-r lg:border-border ${
              chatExpanded ? 'lg:w-[calc(100vw-256px-20%)]' : 'lg:w-[480px]'
            }`}
            initial={isMobile ? { x: 0, y: '100%' } : { x: '-100%', y: 0 }}
            animate={{ x: 0, y: 0 }}
            exit={isMobile ? { x: 0, y: '100%' } : { x: '-100%', y: 0 }}
            transition={{ type: 'tween', duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          >
      {/* Header */}
      <div className="h-16 flex items-center gap-3 px-4 border-b border-border flex-shrink-0">
        <div className="relative">
          {isDM ? renderAvatar(headerAvatarName, headerAvatarUrl) : renderGroupAvatar()}
          {isDM && (
            <span
              className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-surface ${
                isOnline ? 'bg-green-500' : 'bg-zinc-400'
              }`}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-text truncate">{headerName}</p>
          <div className="flex items-center gap-1">
            {isDM ? (
              <span className={`text-xs ${isOnline ? 'text-green-500' : 'text-muted'}`}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            ) : (
              <span className="text-xs text-muted">
                {conv?.members.length ?? 0} members
                {conv?.project ? ` · ${conv.project.name}` : ''}
              </span>
            )}
          </div>
        </div>

        {/* Expand/Collapse button - only on desktop */}
        <button
          onClick={() => setChatExpanded(!chatExpanded)}
          className="hidden lg:block p-1 text-muted hover:text-text transition-colors flex-shrink-0"
          aria-label={chatExpanded ? 'Collapse chat' : 'Expand chat'}
          title={chatExpanded ? 'Collapse chat' : 'Expand chat'}
        >
          {chatExpanded ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          )}
        </button>

        <button
          onClick={() => setActiveConversationId(null)}
          className="p-1 text-muted hover:text-text transition-colors flex-shrink-0"
          aria-label="Close chat"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-4 min-h-0"
      >
        {chatHasMore[convId] && (
          <button
            onClick={() => loadMoreMessages(convId)}
            className="w-full text-xs text-muted hover:text-primary text-center py-1 transition-colors"
          >
            Load earlier messages
          </button>
        )}

        {activeMessages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-text">Start a conversation</p>
            <p className="text-xs text-muted mt-1">
              {isDM
                ? `Say hello to ${otherMember?.name?.split(' ')[0] ?? 'them'}`
                : 'Send the first message to the group'}
            </p>
          </div>
        )}

        {groupedMessages.map(({ label, messages }) => (
          <div key={label}>
            <div className="flex items-center gap-2 my-2">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] text-muted font-medium px-2">{label}</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <div className="space-y-1">
              {messages.map((msg, idx) => {
                const isOwn = msg.senderId === user?.id;
                const prevMsg = idx > 0 ? messages[idx - 1] : null;
                const sameAuthor = prevMsg?.senderId === msg.senderId;
                const isEditing = editingMessageId === msg.id;
                const isDeleted = !!msg.deletedAt;

                // In groups, show sender name/avatar for other people's messages
                const sender = !isOwn && !isDM ? getSenderInfo(msg.senderId) : null;

                return (
                  <div
                    key={msg.id}
                    className={`group flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'} ${
                      sameAuthor ? 'mt-0.5' : 'mt-2'
                    }`}
                    onTouchStart={() => !isEditing && !isDeleted && handleTouchStart(msg.id)}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchCancel}
                  >
                    {/* Avatar — only show for first in a run from this author */}
                    <div className="w-6 flex-shrink-0">
                      {!sameAuthor && !isOwn && (
                        isDM
                          ? renderAvatar(otherMember?.name ?? '?', otherMember?.avatarUrl, true)
                          : renderAvatar(sender?.name ?? '?', sender?.avatarUrl, true)
                      )}
                    </div>

                    <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-[85%]`}>
                      {/* Sender name for groups (first message in a run) */}
                      {!isDM && !isOwn && !sameAuthor && sender && (
                        <span className="text-[10px] font-medium text-muted mb-0.5 ml-1">
                          {sender.name.split(' ')[0]}
                        </span>
                      )}

                      {isDeleted ? (
                        /* Deleted message placeholder */
                        <div className={`px-3 py-2 rounded-2xl text-sm italic ${
                          isOwn
                            ? 'bg-surface-light/50 text-muted rounded-br-sm'
                            : 'bg-surface-light/50 text-muted rounded-bl-sm'
                        }`}>
                          <span className="flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                            Message deleted
                          </span>
                        </div>
                      ) : isEditing ? (
                        /* Inline edit mode */
                        <div className="w-full">
                          <textarea
                            ref={editInputRef}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            rows={1}
                            className="w-full bg-surface-light text-sm text-text rounded-xl px-3 py-2 resize-none outline-none border border-primary/50 focus:border-primary max-h-24 leading-5"
                            style={{ height: 'auto' }}
                            onInput={(e) => {
                              const el = e.currentTarget;
                              el.style.height = 'auto';
                              el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
                            }}
                          />
                          <div className="flex items-center gap-2 mt-1">
                            <button
                              onClick={cancelEdit}
                              className="text-[10px] text-muted hover:text-text transition-colors"
                            >
                              Cancel
                            </button>
                            <span className="text-[10px] text-muted">·</span>
                            <button
                              onClick={submitEdit}
                              disabled={!editValue.trim()}
                              className="text-[10px] text-primary hover:text-primary/80 disabled:opacity-40 transition-colors"
                            >
                              Save
                            </button>
                            <span className="text-[10px] text-muted ml-1">Esc to cancel · Enter to save</span>
                          </div>
                        </div>
                      ) : (
                        /* Normal message bubble */
                        <div className="relative">
                          <div
                            className={`px-3 py-2 rounded-2xl text-sm leading-relaxed break-words ${
                              isOwn
                                ? 'bg-primary text-white rounded-br-sm'
                                : 'bg-surface-light text-text rounded-bl-sm'
                            } ${sameAuthor ? (isOwn ? 'rounded-tr-2xl' : 'rounded-tl-2xl') : ''}`}
                          >
                            {renderMessageContent(msg.content, isOwn)}
                          </div>

                          {/* Hover action button */}
                          {!isDeleted && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openMessageActions(msg.id);
                              }}
                              className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 text-muted hover:text-text transition-opacity ${
                                isOwn ? '-left-7' : '-right-7'
                              }`}
                              aria-label="Message actions"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01" />
                              </svg>
                            </button>
                          )}
                        </div>
                      )}

                      {/* Reactions display with animations (Enhancement #2) */}
                      {!isEditing && !isDeleted && msg.reactions && msg.reactions.length > 0 && (
                        <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          <AnimatePresence mode="popLayout">
                            {msg.reactions.map((reaction) => (
                              <motion.button
                                key={reaction.emoji}
                                layout
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.5, opacity: 0 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                onClick={() => handleReactionClick(msg.id, reaction.emoji, reaction.userReacted)}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
                                  reaction.userReacted
                                    ? 'bg-primary/20 border border-primary/40 text-primary'
                                    : 'bg-surface-light border border-border text-muted hover:border-primary/40'
                                }`}
                                title={reaction.users.map((u) => u.name).join(', ')}
                              >
                                <span>{reaction.emoji}</span>
                                <motion.span
                                  key={reaction.count}
                                  initial={{ scale: 1.3 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                                  className="text-[10px]"
                                >
                                  {reaction.count}
                                </motion.span>
                              </motion.button>
                            ))}
                          </AnimatePresence>
                        </div>
                      )}

                      {/* Quick reactions bar on hover (desktop) or long-press (mobile) */}
                      {!isEditing && !isDeleted && (
                        <>
                          {/* Desktop hover - hidden on mobile */}
                          <div className={`hidden md:flex opacity-0 group-hover:opacity-100 transition-opacity mt-1 gap-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            {QUICK_REACTIONS.map((emoji) => {
                              const existingReaction = msg.reactions?.find((r) => r.emoji === emoji);
                              const alreadyReacted = existingReaction?.userReacted || false;
                              return (
                                <button
                                  key={emoji}
                                  onClick={() => handleReactionClick(msg.id, emoji, alreadyReacted)}
                                  className="w-6 h-6 flex items-center justify-center rounded-full bg-surface-light hover:bg-surface-light/80 border border-border hover:border-primary/40 transition-all text-sm"
                                  title={`React with ${emoji}`}
                                >
                                  {emoji}
                                </button>
                              );
                            })}
                            {/* Full emoji picker button (Enhancement #3) */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setReactionPickerMessageId(msg.id);
                              }}
                              className="w-6 h-6 flex items-center justify-center rounded-full bg-surface-light hover:bg-surface-light/80 border border-border hover:border-primary/40 transition-all text-sm"
                              title="More reactions"
                            >
                              <svg className="w-3 h-3 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          </div>

                          {/* Full emoji picker popover (Enhancement #3) */}
                          {reactionPickerMessageId === msg.id && (
                            <>
                              {/* Backdrop */}
                              <div 
                                className="fixed inset-0 bg-black/30 z-[99]"
                                onClick={() => setReactionPickerMessageId(null)}
                              />
                              {/* Picker (portaled to document.body, no wrapper needed) */}
                              <EmojiPicker
                                onEmojiSelect={async (emoji) => {
                                  const existingReaction = msg.reactions?.find((r) => r.emoji === emoji);
                                  const alreadyReacted = existingReaction?.userReacted || false;
                                  await handleReactionClick(msg.id, emoji, alreadyReacted);
                                  setReactionPickerMessageId(null);
                                }}
                                isOpen={true}
                                hideButton={true}
                              />
                            </>
                          )}
                        </>
                      )}

                      {/* Timestamp + read receipt + edited indicator */}
                      {!isEditing && !isDeleted && (!messages[idx + 1] || messages[idx + 1].senderId !== msg.senderId) && (
                        <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'flex-row-reverse' : ''}`}>
                          <span className="text-[10px] text-muted">{formatTime(msg.createdAt)}</span>
                          {msg.editedAt && (
                            <span className="text-[10px] text-muted italic">edited</span>
                          )}
                          {isOwn && (
                            <svg
                              className={`w-3 h-3 ${msg.readAt ? 'text-primary' : 'text-muted'}`}
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {msg.readAt ? (
                                // Double check (read)
                                <path d="M1.5 12.5l5 5L18.5 5.5M7 12.5l5 5 7-12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                              ) : (
                                // Single check (sent)
                                <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                              )}
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {activeAiSuggestions.length > 0 && (
          <div className="space-y-2">
            {activeAiSuggestions.map((suggestion) => {
              const suggestionText = suggestion.intent === 'CREATE_APPROVAL'
                ? suggestion.draft.approvalText
                : suggestion.draft.commentText;
              const intentLabel = suggestion.intent === 'CREATE_APPROVAL' ? 'Approval' : 'Comment';
              const sourceLabel = suggestion.source === 'LLM'
                ? 'AI'
                : suggestion.source === 'HYBRID'
                  ? 'Hybrid'
                  : 'Rule';

              // ── Unified inline picker (all conversation types) ────────────
              const picker = pickerStates.get(suggestion.id);
              const isCreateMode = picker?.selectedCutId === null;
              const createName = (picker?.cutSearchQuery.trim() || picker?.createCutName.trim()) ?? '';
              // For DMs in create-mode, we also need a project selected
              const isDMConv = !conv?.projectId;
              const resolvedVibeId = picker?.selectedVibeId ?? (picker?.vibeOptions.length === 1 ? picker.vibeOptions[0].id : null);
              const dmProjectResolved = !isDMConv || !isCreateMode || !!picker?.selectedProjectId;
              const canAccept = !!picker && !picker.inferring && (
                (!isCreateMode && picker.selectedCutId !== null) ||
                (isCreateMode && createName.length > 0 && resolvedVibeId !== null && dmProjectResolved)
              );
              const isSubmitting = submittingSuggestionId === suggestion.id;

              // Filtered cut list
              const filteredCuts = picker
                ? picker.cutOptions.filter((c) =>
                    !picker.cutSearchQuery.trim() ||
                    c.name.toLowerCase().includes(picker.cutSearchQuery.toLowerCase()),
                  )
                : [];

              // Chip label shown when collapsed
              const chipLabel = picker
                ? (isCreateMode
                    ? (createName ? `Create "${createName}"` : 'New cut')
                    : (picker.cutOptions.find((c) => c.id === picker.selectedCutId)?.name ?? 'Select cut'))
                : '…';

              return (
                <div
                  key={suggestion.id}
                  className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2"
                >
                  {/* Header row */}
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-primary">AI Suggestion · {intentLabel}</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted">
                        {sourceLabel}
                      </span>
                      <span className="text-[10px] text-muted">
                        {Math.round(suggestion.confidence * 100)}%
                      </span>
                    </div>
                  </div>

                  {/* Entity chips */}
                  <div className="space-y-1">
                    {suggestion.entities.cutName && (
                      <div className="flex items-center gap-2 text-xs">
                        <MapPin className="w-3.5 h-3.5 text-primary/60 flex-shrink-0" />
                        <span className="font-semibold text-primary">{suggestion.entities.cutName}</span>
                        {conv?.project && <span className="text-muted">· {conv.project.name}</span>}
                      </div>
                    )}
                    {suggestion.entities.targetUserName && (
                      <div className="flex items-center gap-2 text-xs">
                        <User className="w-3.5 h-3.5 text-muted flex-shrink-0" />
                        <span className="font-medium text-primary">@{suggestion.entities.targetUserName}</span>
                      </div>
                    )}
                  </div>

                  {/* Draft text */}
                  {suggestionText && (
                    <div className="pt-1 border-t border-border/40">
                      <p className="text-xs text-muted italic">"{suggestionText}"</p>
                    </div>
                  )}

                  {/* ── Inline cut picker ─────────────────────────────── */}
                  <div className="border border-border/60 rounded-lg overflow-hidden bg-surface/50">

                    {/* State 1: Inferring */}
                    {(!picker || picker.inferring) && (
                      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted">
                        <MapPin className="w-3 h-3 flex-shrink-0 animate-pulse" />
                        <span className="animate-pulse">Identifying cut…</span>
                      </div>
                    )}

                    {/* State 2: Create-mode callout — no match found, picker closed */}
                    {picker && !picker.inferring && isCreateMode && !picker.cutPickerOpen && (
                      <div className="px-3 py-3 space-y-2.5">
                        <div className="flex items-start gap-2">
                          <span className="text-sm leading-none mt-0.5">🤔</span>
                          <div>
                            <p className="text-xs font-medium text-amber-500/90">
                              Hmm, we couldn't find{createName ? ` "${createName}"` : ' that cut'}{conv?.projectId ? ' in this project' : ''}.
                            </p>
                            <p className="text-[11px] text-muted mt-0.5">Want to create it as a new cut?</p>
                          </div>
                        </div>

                        {/* DM: project picker — shown before vibe/name when no project is scoped */}
                        {isDMConv && picker.projectOptions.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-[10px] text-muted">Project:</p>
                            <div className="flex flex-wrap gap-1">
                              {picker.projectOptions.map((proj) => {
                                const isProjSelected = picker.selectedProjectId === proj.id;
                                return (
                                  <button
                                    key={proj.id}
                                    type="button"
                                    onClick={() => updatePickerState(suggestion.id, {
                                      selectedProjectId: proj.id,
                                      // Reset vibe selection when project changes
                                      selectedVibeId: proj.vibes.length === 1 ? proj.vibes[0].id : null,
                                      vibeOptions: proj.vibes,
                                      createVibeName: '',
                                    })}
                                    className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${
                                      isProjSelected
                                        ? 'border-primary/60 bg-primary/10 text-primary font-medium'
                                        : 'border-border text-muted hover:text-text hover:border-border/80'
                                    }`}
                                  >
                                    {proj.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Editable cut name */}
                        <div className="flex items-center gap-1.5 bg-surface border border-amber-500/30 rounded-md px-2 py-1 focus-within:border-amber-500/60 transition-colors">
                          <Plus className="w-3 h-3 text-amber-500/70 flex-shrink-0" />
                          <input
                            type="text"
                            value={picker.createCutName}
                            onChange={(e) => updatePickerState(suggestion.id, { createCutName: e.target.value })}
                            placeholder="Cut name…"
                            disabled={isSubmitting}
                            className="flex-1 bg-transparent text-xs text-text placeholder:text-muted outline-none"
                          />
                        </div>

                        {/* Vibe picker */}
                        {picker.vibeOptions.length > 0 && (
                          <div className="space-y-1">
                            <p className={`text-[10px] transition-colors ${
                              isCreateMode && createName.length > 0 && !picker.selectedVibeId
                                ? 'text-amber-500 font-medium animate-pulse'
                                : 'text-muted'
                            }`}>
                              {picker.vibeOptions.length === 1 ? 'Vibe:' : 'Add to vibe:'}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {picker.vibeOptions.map((vibe) => {
                                const resolvedSelectedVibeId = picker.selectedVibeId
                                  ?? (picker.vibeOptions.length === 1 ? picker.vibeOptions[0].id : null);
                                const isVibeSelected = resolvedSelectedVibeId === vibe.id;
                                return (
                                  <button
                                    key={vibe.id}
                                    type="button"
                                    onClick={() => updatePickerState(suggestion.id, { selectedVibeId: vibe.id })}
                                    className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${
                                      isVibeSelected
                                        ? 'border-amber-500/60 bg-amber-500/10 text-amber-500 font-medium'
                                        : 'border-border text-muted hover:text-text hover:border-border/80'
                                    }`}
                                  >
                                    {vibe.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {isDMConv && picker.selectedProjectId && picker.vibeOptions.length === 0 && (
                          <div className="text-[10px] text-amber-500/90">
                            <p>No vibes found in this project yet. Create a vibe first to create this cut.</p>
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <input
                                type="text"
                                value={picker.createVibeName}
                                onChange={(e) => updatePickerState(suggestion.id, { createVibeName: e.target.value })}
                                placeholder="New vibe name…"
                                disabled={picker.creatingVibe || isSubmitting}
                                className="h-7 flex-1 rounded-md border border-amber-500/40 bg-surface px-2 text-[11px] text-text placeholder:text-muted outline-none focus:border-amber-500/70"
                              />
                              <button
                                type="button"
                                onClick={() => void handleCreateVibeInline(suggestion.id, picker)}
                                disabled={picker.creatingVibe || isSubmitting || !picker.createVibeName.trim()}
                                className="h-7 px-2 rounded-md text-[11px] border border-primary/50 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {picker.creatingVibe ? 'Creating…' : 'Create vibe'}
                              </button>
                            </div>
                            <a
                              href={`/projects/${picker.selectedProjectId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-1 text-muted hover:text-primary transition-colors"
                            >
                              Open project page
                            </a>
                          </div>
                        )}

                        {/* Search existing instead */}
                        <button
                          type="button"
                          onClick={() => updatePickerState(suggestion.id, { cutPickerOpen: true })}
                          className="text-[11px] text-muted hover:text-primary transition-colors flex items-center gap-1"
                        >
                          <Search className="w-3 h-3" />
                          Search existing cuts instead
                        </button>
                      </div>
                    )}

                    {/* State 3: Expanded search */}
                    {picker && !picker.inferring && picker.cutPickerOpen && (
                      <div className="px-3 py-2 space-y-2">
                        {/* Header row */}
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted font-medium uppercase tracking-wide">Select cut</span>
                          <button
                            type="button"
                            onClick={() => updatePickerState(suggestion.id, { cutPickerOpen: false })}
                            className="text-[10px] text-muted hover:text-text transition-colors"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Search input */}
                        <div className="flex items-center gap-1.5 bg-surface border border-border rounded-md px-2 py-1">
                          <Search className="w-3 h-3 text-muted flex-shrink-0" />
                          <input
                            autoFocus
                            type="text"
                            placeholder="Search cuts…"
                            value={picker.cutSearchQuery}
                            onChange={(e) => updatePickerState(suggestion.id, { cutSearchQuery: e.target.value })}
                            className="flex-1 bg-transparent text-xs text-text placeholder:text-muted outline-none"
                          />
                        </div>

                        {/* Cut list */}
                        <div className="max-h-40 overflow-y-auto space-y-0.5">
                          {filteredCuts.map((cut) => {
                            const isSelected = picker.selectedCutId === cut.id;
                            return (
                              <button
                                key={cut.id}
                                type="button"
                                onClick={() => updatePickerState(suggestion.id, {
                                  selectedCutId: cut.id,
                                  cutPickerOpen: false,
                                  cutSearchQuery: '',
                                })}
                                className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs transition-colors text-left ${
                                  isSelected
                                    ? 'bg-primary/15 text-primary font-medium'
                                    : 'text-text hover:bg-surface-light'
                                }`}
                              >
                                <span>{cut.name}</span>
                                <span className="flex items-center gap-1.5 flex-shrink-0">
                                  {cut.vibeName && (
                                    <span className="text-[10px] text-muted">{cut.vibeName}</span>
                                  )}
                                  {isSelected && <Check className="w-3 h-3 text-primary" />}
                                </span>
                              </button>
                            );
                          })}

                          {filteredCuts.length === 0 && picker.cutSearchQuery && (
                            <p className="text-xs text-muted px-2 py-1.5">No cuts match "{picker.cutSearchQuery}"</p>
                          )}

                          {filteredCuts.length === 0 && !picker.cutSearchQuery && (
                            <p className="text-xs text-muted px-2 py-1.5">No cuts found yet.</p>
                          )}
                        </div>

                        {/* Divider */}
                        <div className="border-t border-border/40" />

                        {/* Create option */}
                        <button
                          type="button"
                          onClick={() => updatePickerState(suggestion.id, {
                            selectedCutId: null,
                            createCutName: picker.cutSearchQuery.trim() || picker.createCutName,
                            cutPickerOpen: false,
                          })}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-muted hover:bg-surface-light hover:text-text transition-colors text-left"
                        >
                          <Plus className="w-3 h-3 flex-shrink-0" />
                          <span>
                            {picker.cutSearchQuery.trim()
                              ? `Create "${picker.cutSearchQuery.trim()}" as new cut`
                              : 'Create new cut…'}
                          </span>
                        </button>
                      </div>
                    )}

                    {/* State 4: Collapsed — existing cut selected */}
                    {picker && !picker.inferring && !isCreateMode && !picker.cutPickerOpen && (
                      <button
                        type="button"
                        onClick={() => updatePickerState(suggestion.id, { cutPickerOpen: true })}
                        disabled={isSubmitting}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs hover:bg-surface-light/50 transition-colors"
                      >
                        <span className="flex items-center gap-1.5 text-text font-medium">
                          <MapPin className="w-3 h-3 text-primary/60 flex-shrink-0" />
                          {chipLabel}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-muted flex-shrink-0" />
                      </button>
                    )}

                  </div>

                  {/* Timestamp + content fields */}
                  {picker && (
                    <div className="space-y-2 pt-1">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-muted flex-shrink-0" />
                        <span className="text-xs text-muted w-14 flex-shrink-0">
                          {formatSeconds(picker.timestamp)}
                        </span>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={Number.isFinite(picker.timestamp) ? picker.timestamp : 0}
                          onChange={(e) => {
                            const parsed = Number.parseInt(e.target.value, 10);
                            updatePickerState(suggestion.id, {
                              timestamp: Number.isFinite(parsed) ? Math.max(0, parsed) : 0,
                            });
                          }}
                          disabled={isSubmitting}
                          className="flex-1 bg-surface border border-border rounded-md px-2 py-1 text-xs text-text outline-none focus:border-primary/50 disabled:opacity-50"
                          placeholder="seconds"
                        />
                      </div>

                      <textarea
                        value={picker.content}
                        onChange={(e) => updatePickerState(suggestion.id, { content: e.target.value })}
                        rows={2}
                        disabled={isSubmitting}
                        placeholder="Comment text…"
                        className="w-full bg-surface border border-border rounded-md px-2 py-1.5 text-xs text-text resize-none outline-none focus:border-primary/50 placeholder:text-muted disabled:opacity-50"
                      />
                    </div>
                  )}

                  {/* Accept / Ignore */}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => picker && handleAcceptForProject(suggestion, picker)}
                      disabled={!canAccept || isSubmitting}
                      className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
                    >
                      {isSubmitting ? 'Saving…' : 'Accept'}
                    </button>
                    <button
                      onClick={() => dismissAiSuggestion(suggestion.id)}
                      disabled={isSubmitting}
                      className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-surface-light text-text hover:bg-surface-light/80 border border-border disabled:opacity-40 transition-colors"
                    >
                      Ignore
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeAiAcceptedNotices.map((noticeText) => (
          <div key={noticeText} className="flex items-end gap-2">
            <div className="w-6 flex-shrink-0">
              <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center font-semibold text-[10px]">
                AI
              </div>
            </div>
            <div className="flex flex-col items-start max-w-[85%]">
              <span className="text-[10px] font-medium text-muted mb-0.5 ml-1">BandMate AI</span>
              <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-surface-light text-text border border-border/70">
                <p className="text-xs leading-relaxed">{noticeText}</p>
                <button
                  onClick={() => convId && consumeAiAcceptedNotice(convId, noticeText)}
                  className="mt-2 px-2 py-1 text-[10px] rounded-md border border-border text-muted hover:text-text hover:bg-surface"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {isAnyoneTyping && (
          <div className="flex items-end gap-2">
            <div className="w-6 flex-shrink-0">
              {isDM
                ? renderAvatar(otherMember?.name ?? '?', otherMember?.avatarUrl, true)
                : (() => {
                    const typingMember = conv?.members.find((m) => m.id === typingUserIds[0]);
                    return renderAvatar(typingMember?.name ?? '?', typingMember?.avatarUrl, true);
                  })()
              }
            </div>
            <div className="bg-surface-light rounded-2xl rounded-bl-sm px-3 py-2">
              <div className="flex gap-1 items-center">
                <span className="w-1.5 h-1.5 bg-muted rounded-full animate-bounce [animation-delay:0ms]" />
                <span className="w-1.5 h-1.5 bg-muted rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 bg-muted rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
              {!isDM && (
                <span className="text-[9px] text-muted mt-0.5 block">{typingLabel}</span>
              )}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Audio upload picker */}
      {audioUploadPicker && (
        <div className="flex-shrink-0 border-t border-border px-3 py-3 bg-surface">
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
            {/* Header */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4 text-primary" />
                <p className="text-xs font-semibold text-primary">Upload Audio</p>
              </div>
              <button
                onClick={() => setAudioUploadPicker(null)}
                className="p-1 text-muted hover:text-text transition-colors"
                aria-label="Cancel upload"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* File info */}
            <div className="flex items-center gap-2 text-xs text-muted">
              <Upload className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate max-w-[200px]">{audioUploadPicker.file.name}</span>
              <span>({(audioUploadPicker.file.size / (1024 * 1024)).toFixed(1)} MB)</span>
            </div>

            {/* Cut picker */}
            <div className="border border-border/60 rounded-lg overflow-hidden bg-surface/50">
              {/* Collapsed: cut selected */}
              {!audioUploadPicker.cutPickerOpen && (
                <button
                  type="button"
                  onClick={() => updateAudioUploadPicker({ cutPickerOpen: true })}
                  disabled={audioUploadPicker.uploading}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs hover:bg-surface-light/50 transition-colors"
                >
                  <span className="flex items-center gap-1.5 text-text font-medium">
                    <MapPin className="w-3 h-3 text-primary/60 flex-shrink-0" />
                    {audioUploadPicker.selectedCutId
                      ? audioUploadPicker.cutOptions.find((c) => c.id === audioUploadPicker.selectedCutId)?.name ?? 'Select cut'
                      : audioUploadPicker.createCutName
                        ? `Create "${audioUploadPicker.createCutName}"`
                        : 'Select cut'}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-muted flex-shrink-0" />
                </button>
              )}

              {/* Expanded: search/create */}
              {audioUploadPicker.cutPickerOpen && (
                <div className="px-3 py-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted font-medium uppercase tracking-wide">Select cut</span>
                    <button
                      type="button"
                      onClick={() => updateAudioUploadPicker({ cutPickerOpen: false })}
                      className="text-[10px] text-muted hover:text-text transition-colors"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Search input */}
                  <div className="flex items-center gap-1.5 bg-surface border border-border rounded-md px-2 py-1">
                    <Search className="w-3 h-3 text-muted flex-shrink-0" />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search cuts…"
                      value={audioUploadPicker.cutSearchQuery}
                      onChange={(e) => updateAudioUploadPicker({ cutSearchQuery: e.target.value })}
                      className="flex-1 bg-transparent text-xs text-text placeholder:text-muted outline-none"
                    />
                  </div>

                  {/* Cut list */}
                  <div className="max-h-40 overflow-y-auto space-y-0.5">
                    {audioUploadPicker.cutOptions
                      .filter((c) =>
                        !audioUploadPicker.cutSearchQuery.trim() ||
                        c.name.toLowerCase().includes(audioUploadPicker.cutSearchQuery.toLowerCase())
                      )
                      .map((cut) => (
                        <button
                          key={cut.id}
                          type="button"
                          onClick={() => updateAudioUploadPicker({
                            selectedCutId: cut.id,
                            createCutName: '',
                            cutPickerOpen: false,
                            cutSearchQuery: '',
                          })}
                          className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs transition-colors text-left ${
                            audioUploadPicker.selectedCutId === cut.id
                              ? 'bg-primary/15 text-primary font-medium'
                              : 'text-text hover:bg-surface-light'
                          }`}
                        >
                          <span>{cut.name}</span>
                          <span className="flex items-center gap-1.5 flex-shrink-0">
                            {cut.vibeName && (
                              <span className="text-[10px] text-muted">{cut.vibeName}</span>
                            )}
                            {audioUploadPicker.selectedCutId === cut.id && <Check className="w-3 h-3 text-primary" />}
                          </span>
                        </button>
                      ))}
                  </div>

                  {/* Create option */}
                  <div className="border-t border-border/40 pt-2">
                    <button
                      type="button"
                      onClick={() => updateAudioUploadPicker({
                        selectedCutId: null,
                        createCutName: audioUploadPicker.cutSearchQuery.trim() || audioUploadPicker.createCutName,
                        cutPickerOpen: false,
                      })}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-muted hover:bg-surface-light hover:text-text transition-colors text-left"
                    >
                      <Plus className="w-3 h-3 flex-shrink-0" />
                      <span>
                        {audioUploadPicker.cutSearchQuery.trim()
                          ? `Create "${audioUploadPicker.cutSearchQuery.trim()}" as new cut`
                          : 'Create new cut…'}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Create mode inputs */}
            {audioUploadPicker.selectedCutId === null && (
              <div className="space-y-2">
                {/* Cut name input */}
                <div className="flex items-center gap-1.5 bg-surface border border-amber-500/30 rounded-md px-2 py-1 focus-within:border-amber-500/60 transition-colors">
                  <Plus className="w-3 h-3 text-amber-500/70 flex-shrink-0" />
                  <input
                    type="text"
                    value={audioUploadPicker.createCutName}
                    onChange={(e) => updateAudioUploadPicker({ createCutName: e.target.value })}
                    placeholder="Cut name…"
                    disabled={audioUploadPicker.uploading}
                    className="flex-1 bg-transparent text-xs text-text placeholder:text-muted outline-none"
                  />
                </div>

                {/* Vibe picker */}
                {audioUploadPicker.vibeOptions.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-muted">
                      {audioUploadPicker.vibeOptions.length === 1 ? 'Vibe:' : 'Add to vibe:'}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {audioUploadPicker.vibeOptions.map((vibe) => (
                        <button
                          key={vibe.id}
                          type="button"
                          onClick={() => updateAudioUploadPicker({ selectedVibeId: vibe.id })}
                          className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${
                            audioUploadPicker.selectedVibeId === vibe.id
                              ? 'border-amber-500/60 bg-amber-500/10 text-amber-500 font-medium'
                              : 'border-border text-muted hover:text-text hover:border-border/80'
                          }`}
                        >
                          {vibe.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upload button */}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleAudioUpload}
                disabled={
                  audioUploadPicker.uploading ||
                  (!audioUploadPicker.selectedCutId && (!audioUploadPicker.createCutName.trim() || !audioUploadPicker.selectedVibeId))
                }
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
              >
                {audioUploadPicker.uploading ? 'Uploading…' : 'Upload'}
              </button>
              <button
                onClick={() => setAudioUploadPicker(null)}
                disabled={audioUploadPicker.uploading}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-surface-light text-text hover:bg-surface-light/80 border border-border disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="flex-shrink-0 border-t border-border px-3 py-3">
        <div className="relative">
          {/* Mention autocomplete dropdown */}
          {mentionQuery !== null && (mentionLoading || mentionResults.length > 0) && (
            <div className="absolute bottom-full mb-1 left-0 right-0 bg-surface border border-border rounded-xl shadow-lg overflow-hidden z-10">
              {mentionLoading && mentionResults.length === 0 ? (
                <div className="px-3 py-2 text-xs text-muted">Searching…</div>
              ) : (
                mentionResults.map((result) => (
                  <button
                    key={result.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(result);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-surface-light transition-colors"
                  >
                    <span className="text-muted font-medium uppercase tracking-wide w-8 flex-shrink-0">
                      {result.type === 'vibe' ? 'vibe' : 'cut'}
                    </span>
                    <span className="text-text truncate">{result.name}</span>
                  </button>
                ))
              )}
            </div>
          )}
          <div className="flex items-end gap-2 bg-surface-light rounded-xl px-3 py-2">
            <EmojiPicker
              onEmojiSelect={(emoji) => {
                setInputValue((prev) => prev + emoji);
              }}
              buttonClassName="p-1 text-muted hover:text-text transition-colors flex-shrink-0"
              buttonLabel="Add emoji"
            />
            <input
              ref={(el) => { (audioInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el; }}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleAudioFileSelected(file);
                }
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => {
                const input = (audioInputRef as React.MutableRefObject<HTMLInputElement | null>).current;
                input?.click();
              }}
              className="p-1 text-muted hover:text-text transition-colors flex-shrink-0"
              aria-label="Upload audio"
            >
              <Plus className="w-4 h-4" />
            </button>
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setChatInputFocused(true)}
              onBlur={() => setChatInputFocused(false)}
              placeholder={inputPlaceholder}
              rows={1}
              className="flex-1 bg-transparent text-sm text-text placeholder:text-muted resize-none outline-none max-h-24 leading-5"
              style={{ height: 'auto' }}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
              }}
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim() || isSending}
              className="flex-shrink-0 w-7 h-7 rounded-lg bg-primary disabled:opacity-40 flex items-center justify-center transition-opacity hover:bg-primary/90"
              aria-label="Send message"
            >
              <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
        <p className="text-[10px] text-muted mt-1 text-center">Enter to send · Shift+Enter for new line</p>
      </div>
          </motion.div>

    {selectedActionMessage && (
      <ActionSheet
        items={actionMenuItems}
        title="Message Actions"
        open={!!actionMenuMessageId}
        onOpenChange={(open) => {
          if (!open) {
            setActionMenuMessageId(null);
          }
        }}
        hideTrigger={true}
      />
    )}

    {/* Delete confirmation modal */}
    {deletingMessageId && (
      <>
        <div className="fixed inset-0 bg-black/50 z-[70]" onClick={() => setDeletingMessageId(null)} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[80] bg-surface border border-border rounded-xl shadow-xl p-5 w-72">
          <h3 className="text-sm font-semibold text-text mb-2">Delete message?</h3>
          <p className="text-xs text-muted mb-4">
            {isDM
              ? 'This will permanently remove this message for both of you.'
              : 'This will permanently remove this message for everyone.'}
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setDeletingMessageId(null)}
              className="px-3 py-1.5 text-xs text-muted hover:text-text transition-colors rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={executeDelete}
              className="px-3 py-1.5 text-xs text-white bg-red-500 hover:bg-red-600 transition-colors rounded-lg"
            >
              Delete
            </button>
          </div>
        </div>
      </>
    )}


        </>
      )}
    </AnimatePresence>
  );
}
